import React, {
  useEffect, useState
} from 'react';
import {
  useParams, useLocation, useNavigate
} from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector
} from '../../app/hooks';
import {
  Box,
  Pagination,
  CircularProgress
} from '@mui/material';
import { startCase } from 'lodash';
import {
  AppAssetPaths, canUseAuthComponent, AppRoutes, StateStatus
} from '../../app/app.types';
import {
  DataGrid, GridSortModel
} from '@mui/x-data-grid';
import { applicationsListColumns } from '../../constants/tables/resourceTable.types';
import { resourceSelectors } from '../../features/resources/resources.slice';
import { TableState } from '../../constants/tables/table.types';
import { ResourceApplication } from '../../features/resources/resources.model';
import { getResourceApplications } from '../../features/resources/resources.thunks';
import TableRowCount from '../../components/table/TableRowCount';
import { DownloadButton } from '../../components/CustomStyledComponents';
import { ResourceType } from '../../features/resources/resources.model';
import { exportResouceApplicationsCSV } from '../../features/exports/exports.api';
import { getApplicationApprovalList } from '../../features/approvals/approvals.thunks';
import './ResourceApplicantsList.scss';
import {
  ApplicationApproval, ApprovalType
} from '../../features/approvals/approvals.model';
import { approvalSelectors } from '../../features/approvals/approvals.slice';
import { userSelectors } from '../../features/user/user.slice';


interface ResourceApplicantsListProps { }

const ResourceApplicantsList: React.FC<ResourceApplicantsListProps> = () => {

  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = useLocation();
  const user = useAppSelector(userSelectors.selectUser);

  const applicationList = useAppSelector(resourceSelectors.selectResourceApplications);
  const applicationListCount = useAppSelector(resourceSelectors.selectResourceApplicationsCount);
  const resourceApplicationsStatus = useAppSelector(resourceSelectors.selectResourceApplicationsStatus);
  const approvalsList: ApplicationApproval[] = state?.type === ResourceType.scholarship ? useAppSelector(approvalSelectors.selectApplicationApprovalList) : [];

  const [
    tableState,
    setTableState,
  ] = useState<TableState>({
    page: 1,
    pageSize: 100,
    columns: applicationsListColumns,
    rows: [],
    loading: false,
  });

  const [
    isDownloading,
    setIsDownloading,
  ] = useState<boolean>(false);

  const [
    applicationsQuery,
    setApplicationsQuery,
  ] = useState<{
    offset: number;
    limit: number;
    sortModel?: GridSortModel;
  }>({
    offset: 0,
    limit: 100,
    sortModel: undefined,
  });

  useEffect(() => {
    if (!params.resourceId || !state?.type) return;

    setTableState((prev) => ({
      ...prev,
      rows: [],
      loading: true,
    }));

    dispatch(getResourceApplications({
      id: parseInt(params.resourceId, 10),
      type: state.type,
      offset: applicationsQuery.offset,
      limit: applicationsQuery.limit,
      sortModel: applicationsQuery.sortModel,
    }));

    if (state.type === ResourceType.scholarship && canUseAuthComponent(user.roles, AppRoutes.applicationApprovals.authorizedRoles)) {
      dispatch(getApplicationApprovalList({
        offset: 0,
        limit: undefined,
        approvalType: ApprovalType.application,
        sortModel: undefined,
        userType: undefined,
        schoolType: undefined,
      }));
    }
  }, [
    applicationsQuery,
    params.resourceId,
    state?.type,
  ]);

  // Sync loading and rows with Redux status; hide denied applications (approved === false)
  useEffect(() => {
    const isLoading = resourceApplicationsStatus === StateStatus.LOADING;
    const rowsToShow = applicationList.filter((application: ResourceApplication) => application.approved !== false);
    setTableState((prev) => ({
      ...prev,
      loading: isLoading,
      rows: isLoading ? prev.rows : rowsToShow,
    }));
  }, [
    resourceApplicationsStatus,
    applicationList,
  ]);

  return (
    <div id="resource-application-list">
      <Box className="page-content card-background">
        <div className="main-content flex_column">
          <div className="flex_row_jbetween_acenter">
            <h2> {startCase(state.type)} Applications </h2>
            <div className="table-buttons-and-filters flex_jbetween">
              <div className="filters flex_acenter">
                <DownloadButton
                  variant="contained"
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <img src={AppAssetPaths.icons.DOWNLOAD} />
                    )
                  }
                  onClick={async () => {
                    if (params.resourceId && !isDownloading) {
                      setIsDownloading(true);
                      try {
                        await exportResouceApplicationsCSV(state.type, +params.resourceId, state.title);
                      } catch (error) {
                        console.error('Failed to export CSV:', error);
                      } finally {
                        setIsDownloading(false);
                      }
                    }
                  }}
                  disableElevation
                  disabled={isDownloading}
                >
                  {isDownloading ? 'Loading...' : 'Download'}
                </DownloadButton>
              </div>
            </div>
          </div>
        </div>
        <hr></hr>
        <div className="resource-title-and-applicants">
          <span className="resource-title">{state.resourceTitle} {state.title}</span>
          <span className="applicant-count">{applicationListCount} Applications</span>
          <span></span>
        </div>

        <DataGrid
          className="table"
          {...tableState}
          components={{
            Pagination,
          }}
          componentsProps={{
            pagination: {
              page: tableState.page,
              count: Math.ceil(applicationListCount / tableState.pageSize),
              onChange: (_: void, page: number) => {
                setTableState((prev) => ({
                  ...prev,
                  page,
                }));
                setApplicationsQuery((prev) => ({
                  ...prev,
                  offset: tableState.pageSize * (page - 1),
                }));
              },
            },
          }}
          rowCount={applicationListCount}
          onRowClick={(event) => {
            if (state.type === ResourceType.scholarship && event.row.essay) {
              // Cant return to page with no users if rejected
              const rejectReturnPage = (applicationListCount <= 1 && params.resourceId) ?
                AppRoutes.scholarshipDetails.path.replace(':scholarshipId', params.resourceId.toString()) : location.pathname;
              navigate(AppRoutes.scholarshipApplicationDetails.path.replace(':id', event.row.id.toString()), {
                state: {
                  type: ResourceType.scholarship,
                  id: params.resourceId,
                  title: state.title,
                  applicationId: approvalsList.find(o => o.userId.toString() == event.row.id && o.scholarshipId.toString() == params.resourceId)?.id,
                  returnPage: rejectReturnPage,
                },
              });
            }
          }}
          pagination
          paginationMode="server"
          sortingMode="server"
          sortModel={applicationsQuery.sortModel}
          onSortModelChange={(newSortModel: GridSortModel) => {
            setApplicationsQuery((prev) => ({
              ...prev,
              sortModel: newSortModel,
              offset: 0, // Reset to first page when sorting changes
            }));
            setTableState((prev) => ({
              ...prev,
              page: 1, // Reset to first page when sorting changes
            }));
          }}
          disableColumnFilter
          disableColumnMenu
          hideFooterSelectedRowCount
        />

        <TableRowCount
          length={applicationList.length}
          pageSize={tableState.pageSize}
          page={tableState.page}
          totalCount={applicationListCount}
        />
      </Box>
    </div>
  );
};

export default ResourceApplicantsList;
